import httpClient from '@common/api/httpClient';
import paramsSerializer from '@common/api/paramsSerializer';
import {
  type BackupRegisterResponse,
  type DeviceBackup,
  type GetBackupRestoresResponse,
  type PaginatedItemsResponse,
} from '@common/types/api';
import { type GUID, type InstallationId } from '@common/types/brandedTypes';
import { type ListWithCount } from '@common/utils/formatting/paginationQueryHelpers';

export async function fetchBackupRestores(
  installationId: InstallationId,
  signal: AbortSignal,
) {
  try {
    const { data } = await httpClient.get<GetBackupRestoresResponse[]>(
      `/rest/v1/BackupRestores/installation/${installationId}`,
      { signal },
    );
    return data;
  } catch {
    return [];
  }
}

export async function fetchBackupRestoreRegisters(
  restoreId: GUID,
) {
  try {
    const { data } = await httpClient.get<BackupRegisterResponse[]>(
      `/rest/v1/BackupRestores/${restoreId}/registers`,
    );
    return data;
  } catch {
    return [];
  }
}

export async function fetchBackups(
  installationId: InstallationId,
  take: number,
  skip: number,
  signal: AbortSignal,
  filter?: string,
): Promise<ListWithCount<DeviceBackup> | undefined> {
  try {
    const { data } = await httpClient.get<PaginatedItemsResponse<DeviceBackup>>(
      '/rest/v1/Backups',
      {
        params: { installationId, skip, take, filter },
        paramsSerializer,
        signal,
      },
    );
    return {
      items: data.items,
      count: data.totalCount,
    };
  } catch {
    return undefined;
  }
}

export async function fetchBackupRegisters(
  id: GUID,
) {
  const { data } = await httpClient.get<BackupRegisterResponse[]>(
    `/rest/v1/Backups/${id}/registers`,
  );
  return data;
}

export function createBackup(
  installationId: InstallationId,
  retailerId: string | undefined,
  name: string,
  comment: string,
) {
  return httpClient.post('/rest/v1/Backups', {
    installationId,
    retailerId,
    name,
    comment,
  });
}

export async function fetchBackup(
  backupId: GUID,
) {
  const { data } = await httpClient.get<DeviceBackup>(`/rest/v1/Backups/${backupId}`);
  return data;
}

export function updateBackup(
  backupId: string,
  name: string,
  comment: string,
) {
  return httpClient.put(`/rest/v1/Backups/${backupId}`, {
    name,
    comment,
  });
}

export function deleteBackup(id: string) {
  return httpClient.delete(`/rest/v1/Backups/${id}`);
}

export async function restoreBackup(
  id: GUID,
) {
  const { data } = await httpClient.post<{ backupRestoreId: GUID; }>(
    `/rest/v1/Backups/${id}/restore`,
  );
  return data.backupRestoreId;
}
